// Pixi helpers shared by the habitats: gradients drawn as bands and a particle pool.
import { Container, Graphics, GraphicsContext } from "pixi.js";
import { mix } from "../palette";

/** Multi-stop vertical gradient drawn as flat bands (static; draw once per rebuild). */
export function verticalGradient(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  stops: readonly (readonly [number, number])[],
  bands = 32,
): void {
  const bh = h / bands;
  for (let i = 0; i < bands; i++) {
    const t = (i + 0.5) / bands;
    g.rect(x, y + i * bh, w, bh + 1).fill(sampleStops(stops, t));
  }
}

export function sampleStops(stops: readonly (readonly [number, number])[], t: number): number {
  if (t <= stops[0][0]) return stops[0][1];
  for (let i = 1; i < stops.length; i++) {
    const [t1, c1] = stops[i];
    if (t <= t1) {
      const [t0, c0] = stops[i - 1];
      return mix(c0, c1, (t - t0) / Math.max(1e-6, t1 - t0));
    }
  }
  return stops[stops.length - 1][1];
}

export interface Particle {
  g: Graphics;
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
  size: number;
  phase: number;
  seed: number;
}

/**
 * Fixed-size pool of particles sharing one GraphicsContext (one geometry, many transforms).
 * Nothing is allocated after construction.
 */
export class ParticlePool {
  readonly container = new Container();
  readonly items: Particle[] = [];
  private cursor = 0;

  constructor(
    readonly context: GraphicsContext,
    readonly capacity: number,
  ) {
    for (let i = 0; i < capacity; i++) {
      const g = new Graphics(context);
      g.visible = false;
      this.container.addChild(g);
      this.items.push({
        g,
        active: false,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        age: 0,
        life: 1,
        size: 1,
        phase: 0,
        seed: i * 0.6180339,
      });
    }
  }

  get activeCount(): number {
    let n = 0;
    for (const p of this.items) if (p.active) n++;
    return n;
  }

  spawn(): Particle | null {
    for (let k = 0; k < this.capacity; k++) {
      const i = (this.cursor + k) % this.capacity;
      const p = this.items[i];
      if (!p.active) {
        this.cursor = (i + 1) % this.capacity;
        p.active = true;
        p.age = 0;
        p.g.visible = true;
        return p;
      }
    }
    return null;
  }

  kill(p: Particle): void {
    p.active = false;
    p.g.visible = false;
  }

  clear(): void {
    for (const p of this.items) this.kill(p);
  }

  destroy(): void {
    this.container.destroy({ children: true });
    this.context.destroy();
  }
}

/** Cheap deterministic hash → [0,1) for per-particle variation without RNG state. */
export function hash01(n: number): number {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}
